import { inject, Injectable, signal } from '@angular/core';
import { BaseService } from '@fluxonos/features/shared/services/base-service';
import { HttpClient } from '@angular/common/http';
import { environment } from '@fluxonos/environments/environment';
import {
  CustomerOperatorDeviceCreateRequestDto,
  CustomerOperatorDeviceDto,
  CustomerOperatorDeviceFlatDto,
  CustomerOperatorDeviceNetworkScanDto,
  CustomerOperatorDeviceUpdateRequestDto,
  FluxonosUtils,
  FromToRequestDto,
  ILogsSessionCreatedPayload,
  IOperatorMetricsPayload,
  ITerminalSessionCreatedPayload,
  LogsSource,
  SearchRequestDto,
  SearchResponseDto,
  WeatherSeriesDto
} from '@fluxonos/fluxonos-dto';
import { HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class CustomerOperatorDeviceService extends BaseService {
  private readonly _httpClient: HttpClient = inject(HttpClient);

  private readonly _apiBase = environment.features.customerModule.customer;
  private readonly _selectedCustomerOperatorDevice = signal<CustomerOperatorDeviceDto | null>(null);
  private readonly _selectedCustomerOperatorDevice$ = toObservable(this._selectedCustomerOperatorDevice);

  public search(search?: SearchRequestDto): Observable<SearchResponseDto<CustomerOperatorDeviceDto>> {
    const endpoint = this.getEndpoint(this._apiBase.search);

    const params = this.buildHttpParams(search);

    return this._httpClient
      .request<SearchResponseDto<CustomerOperatorDeviceDto>>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        params: params
      })
      .pipe(
        map((res) => {
          res.data = FluxonosUtils.transformToDtoList(CustomerOperatorDeviceDto, res.data);
          return FluxonosUtils.transformToDto(SearchResponseDto, res);
        })
      );
  }

  public searchByModel(
    manufacturerModelId: string,
    search?: SearchRequestDto
  ): Observable<SearchResponseDto<CustomerOperatorDeviceFlatDto>> {
    const endpoint = this.getEndpoint(this._apiBase.searchByModel, manufacturerModelId);

    const params = this.buildHttpParams(search);

    return this._httpClient
      .request<SearchResponseDto<CustomerOperatorDeviceFlatDto>>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        params: params
      })
      .pipe(
        map((res) => {
          res.data = FluxonosUtils.transformToDtoList(CustomerOperatorDeviceFlatDto, res.data);
          return FluxonosUtils.transformToDto(SearchResponseDto, res);
        })
      );
  }

  public create(data: CustomerOperatorDeviceCreateRequestDto): Observable<CustomerOperatorDeviceDto> {
    const endpoint = this.getEndpoint(this._apiBase.create);

    return this._httpClient
      .request<CustomerOperatorDeviceDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        body: data
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(CustomerOperatorDeviceDto, res)));
  }

  public getById(id: string): Observable<CustomerOperatorDeviceDto> {
    const endpoint = this.getEndpoint(this._apiBase.getById, id);

    return this._httpClient
      .request<CustomerOperatorDeviceDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        }
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(CustomerOperatorDeviceDto, res)));
  }

  public deleteById(id: string): Observable<boolean> {
    const endpoint = this.getEndpoint(this._apiBase.deleteById, id);

    return this._httpClient.request<boolean>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header['contentType']
      }
    });
  }

  /**
   * Wipes the plant's measured and derived data (modbus readings, rollup, cumulated counters, cost
   * and PV yield rows); the configuration stays. Module admins only — it cannot be undone.
   * Resolves with the number of rows removed per table.
   */
  public resetAllData(id: string): Observable<Record<string, number>> {
    const endpoint = this.getEndpoint(this._apiBase.resetAllData, id);

    return this._httpClient.request<Record<string, number>>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header['contentType']
      }
    });
  }

  /** the plant's operator scans its local network (IP / MAC / vendor / hostname / Modbus TCP) — takes ~10–30 s */
  public networkScan(id: string): Observable<CustomerOperatorDeviceNetworkScanDto> {
    const endpoint = this.getEndpoint(this._apiBase.networkScan, id);

    return this._httpClient
      .request<CustomerOperatorDeviceNetworkScanDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        }
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(CustomerOperatorDeviceNetworkScanDto, res)));
  }

  public patchById(id: string, data: CustomerOperatorDeviceUpdateRequestDto): Observable<CustomerOperatorDeviceDto> {
    const endpoint = this.getEndpoint(this._apiBase.patchById, id);

    return this._httpClient
      .request<CustomerOperatorDeviceDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        body: data
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(CustomerOperatorDeviceDto, res)));
  }

  public weatherForecast(id: string, fromTo: FromToRequestDto): Observable<WeatherSeriesDto> {
    const endpoint = this.getEndpoint(this._apiBase.weatherForecast, id);
    const params = new HttpParams().set('from', fromTo.from.toISOString()).set('to', fromTo.to.toISOString());

    return this._httpClient
      .request<WeatherSeriesDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        params
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(WeatherSeriesDto, res)));
  }

  public weatherHistorical(id: string, fromTo: FromToRequestDto): Observable<WeatherSeriesDto> {
    const endpoint = this.getEndpoint(this._apiBase.weatherHistorical, id);
    const params = new HttpParams().set('from', fromTo.from.toISOString()).set('to', fromTo.to.toISOString());

    return this._httpClient
      .request<WeatherSeriesDto>(endpoint.method, endpoint.url, {
        headers: {
          'Content-Type': endpoint.header['contentType']
        },
        params
      })
      .pipe(map((res) => FluxonosUtils.transformToDto(WeatherSeriesDto, res)));
  }

  public operatorConnected(id: string): Observable<boolean> {
    const endpoint = this.getEndpoint(this._apiBase.operator.connected, id);

    return this._httpClient.request<boolean>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header['contentType']
      }
    });
  }

  public operatorUpdate(id: string): Observable<boolean> {
    const endpoint = this.getEndpoint(this._apiBase.operator.update, id);

    return this._httpClient.request<boolean>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header['contentType']
      }
    });
  }

  public getLatestOperatorVersion(id: string): Observable<{ version: string | null }> {
    const endpoint = this.getEndpoint(this._apiBase.operator.latestVersion, id);

    return this._httpClient.request<{ version: string | null }>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header['contentType']
      }
    });
  }

  public createTerminalSession(id: string): Observable<ITerminalSessionCreatedPayload> {
    const endpoint = this.getEndpoint(this._apiBase.terminal.createSession, id);

    return this._httpClient.request<ITerminalSessionCreatedPayload>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header['contentType']
      }
    });
  }

  public createLogsSession(id: string, source: LogsSource): Observable<ILogsSessionCreatedPayload> {
    const endpoint = this.getEndpoint(this._apiBase.logs.createSession, id);
    const params = new HttpParams().set('source', source);

    return this._httpClient.request<ILogsSessionCreatedPayload>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header['contentType']
      },
      params
    });
  }

  public getMetricsHistory(id: string): Observable<IOperatorMetricsPayload[]> {
    const endpoint = this.getEndpoint(this._apiBase.metrics.history, id);

    return this._httpClient.request<IOperatorMetricsPayload[]>(endpoint.method, endpoint.url, {
      headers: {
        'Content-Type': endpoint.header['contentType']
      }
    });
  }

  public setSelectedCustomerOperatorDevice(customerOperatorDevice: CustomerOperatorDeviceDto | null): void {
    this._selectedCustomerOperatorDevice.set(customerOperatorDevice);
  }

  public cleanUp(): void {
    this._selectedCustomerOperatorDevice.set(null);
  }

  public get selectedCustomerOperatorDevice(): CustomerOperatorDeviceDto | null {
    return this._selectedCustomerOperatorDevice.asReadonly()();
  }

  public get selectedCustomerOperatorDevice$(): Observable<CustomerOperatorDeviceDto | null> {
    return this._selectedCustomerOperatorDevice$;
  }
}
